import { Icon, Stack, StackProps, useTheme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { PeriodTypeEnum } from '@schooly/api';
import { CalendarDoneIcon, CoffeeIcon, StarIcon, StudentIcon } from '@schooly/style';
import { FC } from 'react';

import { RegisterSessionEnum } from '@schooly/api';
import { getPeriodTypePalette } from '../../utils';

const ICON_SIZE = {
  big: 40,
  small: 26,
} as const;

type IconSize = keyof typeof ICON_SIZE;
interface TimetableStructurePeriodAvatarProps extends StackProps {
  type: PeriodTypeEnum;
  registerSession?: RegisterSessionEnum | null;
}

export const TimetableStructurePeriodAvatar: FC<TimetableStructurePeriodAvatarProps> = ({
  type,
  registerSession,
  sx,
  ...props
}) => {
  const theme = useTheme();

  const renderIcon = (type: PeriodTypeEnum) => {
    switch (type) {
      case PeriodTypeEnum.Lesson:
        return <StudentIcon />;
      case PeriodTypeEnum.Registration:
        return <CalendarDoneIcon />;
      case PeriodTypeEnum.Activity:
        return <StarIcon />;
      case PeriodTypeEnum.Break:
        return <CoffeeIcon />;
    }
  };

  const withRegistration = type === PeriodTypeEnum.Lesson && registerSession != null;

  const renderIconWithBackground = (type: PeriodTypeEnum, size: IconSize = 'big') => {
    const palette = getPeriodTypePalette(type, theme);

    return (
      <Stack
        sx={(theme) => ({
          position: 'relative',
          width: ICON_SIZE[size],
          height: ICON_SIZE[size],
          borderRadius: '50%',
          backgroundColor: palette.secondary,
          alignItems: 'center',
          justifyContent: 'center',
        })}
        {...props}
      >
        <Icon sx={{ color: palette.primary }} fontSize={size === 'small' ? 'small' : undefined}>
          {renderIcon(type)}
        </Icon>
      </Stack>
    );
  };

  if (!withRegistration)
    return (
      <Stack
        sx={(theme) => ({
          flexShrink: 0,
          ...((typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject),
        })}
      >
        {renderIconWithBackground(type)}
      </Stack>
    );

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      width={ICON_SIZE.big}
      height={ICON_SIZE.big}
      position="relative"
      sx={(theme) => ({
        flexShrink: 0,
        ...((typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject),
      })}
    >
      {renderIconWithBackground(type, 'small')}
      <Stack position={'absolute'} right={0}>
        {renderIconWithBackground(PeriodTypeEnum.Registration, 'small')}
      </Stack>
    </Stack>
  );
};
